/**
 * The material order: the editor, placing it, generating one from the
 * roof, copying one off another estimate, and the CompanyCam receipt
 * picker (ADR 0325). What goes to the supplier — the email, the PDF, the
 * printed copy — stays in English.
 */
export const orders: Readonly<Record<string, string>> = {
  // Shared words
  Cancel: "Cancelar",
  Close: "Cerrar",
  Delete: "Eliminar",
  Done: "Listo",
  Copy: "Copiar",
  "Copying…": "Copiando…",
  Total: "Total",
  Subtotal: "Subtotal",
  Supplier: "Proveedor",
  Order: "Orden",
  Lines: "Líneas",
  From: "De",
  What: "Qué",
  Includes: "Incluye",
  "Supplier / crew": "Proveedor / cuadrilla",
  Template: "Plantilla",
  Package: "Paquete",
  Trade: "Oficio",
  Status: "Estado",
  Actions: "Acciones",
  Undo: "Deshacer",
  or: "o",

  // The picker: a receipt out of the job's CompanyCam photos
  "Receipt from CompanyCam": "Recibo desde CompanyCam",
  "Credit memo from CompanyCam": "Nota de crédito desde CompanyCam",
  "This job's photos, newest first. Tap {what} to read it.":
    "Las fotos de este trabajo, las más recientes primero. Toca {what} y se leerá.",
  "the receipt": "el recibo",
  "the credit memo": "la nota de crédito",
  "This job has no CompanyCam project yet, so there are no photos to pick from.":
    "Este trabajo aún no tiene proyecto en CompanyCam, así que no hay fotos para elegir.",
  "No photos on this job's CompanyCam project yet.":
    "Aún no hay fotos en el proyecto de CompanyCam de este trabajo.",
  "Photo taken {when}": "Foto tomada {when}",
  "Getting it…": "Obteniendo…",
  "Loading photos…": "Cargando fotos…",
  "Show older photos": "Mostrar fotos anteriores",

  // Copying an order off another estimate
  "No supplier": "Sin proveedor",
  "No crew": "Sin cuadrilla",
  "1 expense": "1 gasto",
  "{n} expenses": "{n} gastos",
  "Copy {noun}": "Copiar {noun}",
  "Material Order": "Orden de materiales",
  "Work Order": "Orden de trabajo",
  "Other Expenses": "Otros gastos",
  "material order": "orden de materiales",
  "work order": "orden de trabajo",
  "other expenses": "otros gastos",
  "From another estimate on job #{number}. Nothing on the estimate it comes from is changed.":
    "Desde otro presupuesto del trabajo #{number}. Nada cambia en el presupuesto de origen.",
  "Reading the other estimates…": "Leyendo los otros presupuestos…",
  "This job has only one estimate, so there is nothing to copy from.":
    "Este trabajo tiene un solo presupuesto, así que no hay de dónde copiar.",
  "The other estimate has no {noun} to copy.":
    "El otro presupuesto no tiene {noun} para copiar.",
  "The other estimates have no {noun} to copy.":
    "Los otros presupuestos no tienen {noun} para copiar.",
  "A copy starts at the beginning: not placed, not scheduled, nothing paid and nothing reconciled, whatever the order it came from had got to. Change-order lines are left behind — they were agreed on that estimate.":
    "Una copia empieza desde cero: sin colocar, sin programar, nada pagado y nada conciliado, sin importar hasta dónde había llegado la orden original. Las líneas de orden de cambio se quedan atrás: se acordaron en ese presupuesto.",

  // An order worked out from the roof
  "This job has no wall measurements yet. Add them on the Wall Measurements card first.":
    "Este trabajo aún no tiene medidas de paredes. Agrégalas primero en la tarjeta Medidas de paredes.",
  "This job has no roof measurements yet. Take them on the Roof Measurement step first.":
    "Este trabajo aún no tiene medidas del techo. Tómalas primero en el paso Medida del techo.",
  '"{name}" has no products on it yet — add them under Tools → Templates.':
    '"{name}" aún no tiene productos — agrégalos en Herramientas → Plantillas.',
  'Nothing on "{name}" applies to the build you described.':
    'Nada de "{name}" aplica a la construcción descrita.',
  '"{name}" produced no material lines for this roof.':
    '"{name}" no produjo líneas de material para este techo.',
  '"{name}" produced no labour lines for this roof.':
    '"{name}" no produjo líneas de mano de obra para este techo.',
  "Check before the order is sent:": "Revisar antes de enviar la orden:",
  "{n} line(s) could not be worked out:":
    "{n} línea(s) no se pudieron calcular:",
  "Created. {report}": "Creado. {report}",
  "{noun} From Measurements": "{noun} desde medidas",
  "Quantities off this roof, from a template. None of the template's wording comes with them.":
    "Cantidades de este techo, según una plantilla. Nada del texto de la plantilla viene con ellas.",
  "Generating…": "Generando…",
  "Create {noun}": "Crear {noun}",
  "Shingle, metal or flat": "Teja, metal o plano",
  Shingle: "Teja",
  Metal: "Metal",
  "Flat & Other": "Plano y otros",
  Siding: "Revestimiento",
  Decks: "Decks",
  "{path} packages": "Paquetes de {path}",
  "Still being built": "Aún en construcción",
  "This template has not been filled in yet, so an estimate generated from it will be missing most of its lines.":
    "Esta plantilla aún no está completa, así que a un presupuesto generado con ella le faltarán casi todas sus líneas.",
  "A template line that names its own supplier keeps it, and gets an order of its own.":
    "Una línea de plantilla que nombra su propio proveedor lo conserva y recibe su propia orden.",
  "This estimate could not be read, so its areas in another material cannot be taken off the roof: {error}":
    "No se pudo leer este presupuesto, así que sus áreas en otro material no se pueden restar del techo: {error}",
  "This estimate could not be found, so an order cannot be sized from it.":
    "No se encontró este presupuesto, así que no se puede dimensionar una orden a partir de él.",
  'This is the estimate\'s "{area}" package, so it is sized to that area alone, as measured when the estimate was generated.':
    'Este es el paquete "{area}" del presupuesto, así que se dimensiona solo a esa área, tal como se midió al generar el presupuesto.',
  "This estimate has areas in another material, so this is sized to the rest of the roof, less those areas, as measured when the estimate was generated.":
    "Este presupuesto tiene áreas en otro material, así que esto se dimensiona al resto del techo, sin esas áreas, tal como se midió al generar el presupuesto.",
  "Measuring from {names} — {squares} squares of siding and trim, {waste}% waste.":
    "Midiendo desde {names} — {squares} cuadros de revestimiento y molduras, {waste}% de desperdicio.",
  "Measuring from {names} — {squares} squares in total, {waste}% waste.":
    "Midiendo desde {names} — {squares} cuadros en total, {waste}% de desperdicio.",
  "the walls": "las paredes",
  "the roof layout": "el plano del techo",
  "No wall measurements on this job yet. Add them on the Wall Measurements card first.":
    "Aún no hay medidas de paredes en este trabajo. Agrégalas primero en la tarjeta Medidas de paredes.",
  "No roof measurements on this job yet. Take them on the Roof Measurement step first.":
    "Aún no hay medidas del techo en este trabajo. Tómalas primero en el paso Medida del techo.",
  "This writes {kind} lines and nothing else. The estimate's line items, its scope of work and its price are left exactly as they are.":
    "Esto escribe líneas de {kind} y nada más. Las partidas del presupuesto, su alcance de trabajo y su precio quedan exactamente como están.",
  material: "material",
  labour: "mano de obra",

  // The material order editor
  "Delete Material Order MO-{n}?": "¿Eliminar la orden de materiales MO-{n}?",
  "Its lines go with it. This cannot be undone.":
    "Sus líneas se van con ella. Esto no se puede deshacer.",
  "Material Order MO-{n}": "Orden de materiales MO-{n}",
  "Materials for this job. Totals feed the estimate's Material Cost line.":
    "Materiales para este trabajo. Los totales alimentan la línea Costo de materiales del presupuesto.",
  "The crew on this job uses a {nailer} framing nail gun.":
    "La cuadrilla de este trabajo usa una clavadora de estructura de {nailer}.",
  Coil: "rollo",
  "Switch to {nailer} nails": "Cambiar a clavos de {nailer}",
  "{present} of {total} {manufacturer} parts on this order":
    "{present} de {total} piezas {manufacturer} en esta orden",
  "it qualifies for {tier}": "califica para {tier}",
  "not enough for an enhanced warranty":
    "no alcanza para una garantía mejorada",
  "{tier} needs one more": "{tier} necesita una más",
  "{tier} needs {n} more": "{tier} necesita {n} más",
  "and the registration fee goes up {fee} ({to}/sq instead of {from}/sq on {squares} squares)":
    "y la cuota de registro sube {fee} ({to}/cuadro en vez de {from}/cuadro en {squares} cuadros)",
  "add {name}": "agregar {name}",
  "{amount} in material": "{amount} en material",
  "Swap it": "Cambiarla",
  "Could not read the supplier's prices, so the material figure is not shown: {error}":
    "No se pudieron leer los precios del proveedor, así que no se muestra la cifra de material: {error}",
  "Sales Tax": "Impuesto sobre ventas",
  typed: "escrito a mano",
  "use the rate": "usar la tasa",
  "Delivery Date": "Fecha de entrega",
  "Delivery Note": "Nota de entrega",
  Pickup: "Recoger",
  "Hold Order": "Retener orden",
  "Change Order": "Orden de cambio",
  // What `orderStatusLabel` can say
  "Not placed": "Sin colocar",
  Ordered: "Pedido",
  "Picked up": "Recogido",
  "Not scheduled": "Sin programar",
  Scheduled: "Programado",
  Completed: "Completado",
  Paid: "Pagado",
  // The checklist
  "This Order": "Esta orden",
  "Order placed": "Orden colocada",
  "Place order": "Colocar orden",
  Delivered: "Entregado",
  "Mark picked up": "Marcar recogido",
  "Mark delivered": "Marcar entregado",
  "Mark paid": "Marcar pagado",
  "Send again": "Enviar de nuevo",
  "Emailed to {to} on {when}.": "Enviada por email a {to} el {when}.",
  "the supplier": "el proveedor",
  "Marked placed by hand — no email was sent from here.":
    "Marcada como colocada a mano — no se envió ningún email desde aquí.",
  "Choose a supplier before placing this order.":
    "Elige un proveedor antes de colocar esta orden.",
  "It is under Supplier, above.": "Está en Proveedor, arriba.",
  "Fill these in before the order goes: 1 line still need a color":
    "Completa esto antes de que salga la orden: 1 línea aún necesita color",
  "Fill these in before the order goes: {n} lines still need a color":
    "Completa esto antes de que salga la orden: {n} líneas aún necesitan color",
  // Actions
  "Print For The Supplier": "Imprimir para el proveedor",
  "Print Without Prices": "Imprimir sin precios",
  "Duplicate This Order": "Duplicar esta orden",
  "Read A Supplier Quote": "Leer una cotización del proveedor",
  "Last checked {when} against {file}": "Última revisión {when} contra {file}",
  "Last checked {when}": "Última revisión {when}",
  "Save PDF To Docs": "Guardar PDF en Documentos",
  "Update Supplier Pricing": "Actualizar precios del proveedor",
  "Pull Latest Pricing": "Traer los últimos precios",
  "Push To QuickBooks": "Enviar a QuickBooks",
  "Greyed items need something that is not built yet — see the note below.":
    "Los elementos en gris necesitan algo que aún no está construido — ver la nota abajo.",
  // Reading a receipt against the order
  "{file} could not be saved to Docs.":
    "No se pudo guardar {file} en Documentos.",
  "The order was still checked against it.":
    "Aun así, la orden se revisó contra él.",
  "The file itself is on Docs and on this order.":
    "El archivo está en Documentos y en esta orden.",
  "{file} could not be read.": "No se pudo leer {file}.",
  "Check the order by eye.": "Revisa la orden a ojo.",
  "Nothing could be read from {file}.": "No se pudo leer nada de {file}.",
  "It may not be a receipt at all. Check the order by eye.":
    "Puede que no sea un recibo. Revisa la orden a ojo.",
  "Reading…": "Leyendo…",
  "Upload Receipt / Invoice": "Subir recibo / factura",
  "Receipt From CompanyCam": "Recibo desde CompanyCam",
  "Filed on this order and on the job’s Docs card:":
    "Archivado en esta orden y en la tarjeta Documentos del trabajo:",
  "Checking…": "Revisando…",
  "Check against the order": "Revisar contra la orden",
  // Placing the order
  "Place Material Order": "Colocar orden de materiales",
  "Email the order to the supplier as a PDF, or send it your own way and record that it went out.":
    "Envía la orden al proveedor por email como PDF, o envíala por otro medio y registra que salió.",
  "Copied!": "¡Copiado!",
  "Copy order": "Copiar orden",
  "Mark as placed": "Marcar como colocada",
  "Sending…": "Enviando…",
  "Email & mark placed": "Enviar email y marcar colocada",
  "Your Google account is not connected, so this cannot be emailed from here. Copy the order and send it your own way, then Mark as placed.":
    "La cuenta de Google no está conectada, así que esto no se puede enviar por email desde aquí. Copia la orden, envíala por otro medio y luego Marcar como colocada.",
  "This email asks {supplier} to credit the QuickMeasure report for this roof. It is asked once per job — send it and it will not be asked again.":
    "Este email pide a {supplier} que acredite el reporte QuickMeasure de este techo. Se pide una vez por trabajo — envíalo y no se volverá a pedir.",
  To: "Para",
  "No order email for {supplier} — add one in Tools → Suppliers":
    "No hay email de pedidos para {supplier} — agrega uno en Herramientas → Proveedores",
  "Supplier email": "Email del proveedor",
  Subject: "Asunto",
  Attached: "Adjunto",
  "{file} — the order itself, with prices":
    "{file} — la orden misma, con precios",
  "Measurement report": "Reporte de medidas",
  "Attach the measurement report": "Adjuntar el reporte de medidas",
  "Attach {file}": "Adjuntar {file}",
  "None on this job. Upload one on Roof Measurements and it goes with the order.":
    "Ninguno en este trabajo. Sube uno en Medidas del techo y va con la orden.",
};
